import React from "react";
import { Link } from "react-router-dom";
import { Gamepad2, Gift, Package, Sparkles } from "lucide-react";
import { ProductGrid } from "./ProductGrid";
import { Reveal } from "./Reveal";

const BUNDLE_IDS = ["digilib-world-pakiet", "digilib-pakiet-kids"];
const GAME_NAMES = ["Matematyka", "Angielski", "Geografia", "Przyroda", "Historia", "Polski", "Kodowanie"];

export function BundleCard({ testid, img, badge, title, desc, items, price, oldPrice, note, tall, soon = true, href }) {
  return (
    <Reveal>
      <div className="glow-border group rounded-3xl p-[2px]" data-testid={testid}>
        <div className={`relative overflow-hidden rounded-3xl bg-slate-950 ${tall ? "min-h-[360px]" : "min-h-[300px]"}`}>
          <img src={img} alt="" aria-hidden="true" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-45 transition-all duration-700 group-hover:scale-105 group-hover:opacity-75 group-hover:saturate-150" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/25" />
          <div className="relative flex h-full flex-col justify-between gap-6 p-7 sm:p-10">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-950">
                {badge}
              </span>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{title}</h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-300">{desc}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {items.map((it) => (
                  <span key={it} className="rounded-full border border-white/15 bg-slate-950/60 px-3 py-1 text-[11px] font-semibold text-slate-200 backdrop-blur">
                    {it}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-display text-4xl font-extrabold text-white sm:text-5xl">
                  {price} <span className="text-xl">zł</span>
                  <span className="ml-3 align-middle text-base font-semibold text-slate-400 line-through">{oldPrice}</span>
                </p>
                <p className="mt-1 text-xs font-semibold text-amber-300">{note}</p>
              </div>
              {soon ? (
                <span className="rounded-full border border-violet-400/50 bg-violet-400/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-violet-300">
                  Premiera wkrótce
                </span>
              ) : (
                <Link
                  to={href}
                  data-testid={`${testid}-cta`}
                  className="rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 px-6 py-2.5 text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
                >
                  Zobacz pakiet
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

// Sekcja GRY EDUKACYJNE — osobna kategoria obok aplikacji: banner WOW, wielkie pakiety, siatka gier.
export function GamesSection({ games }) {
  if (!games.length) return null;
  const singles = games.filter((g) => !BUNDLE_IDS.includes(g.id));
  const bundleGames = games.find((g) => g.id === "digilib-world-pakiet");
  const bundleKids = games.find((g) => g.id === "digilib-pakiet-kids");

  return (
    <section id="gry" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6" data-testid="games-section">
      <Reveal>
        <div className="glow-border group rounded-3xl p-[2px]">
          <div className="relative overflow-hidden rounded-3xl bg-slate-950">
            <img
              src="/img/banners/digilib-world-campaign.jpeg"
              alt="Świat gier edukacyjnych DigiLib World"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-45 transition-all duration-700 group-hover:scale-105 group-hover:opacity-75 group-hover:saturate-150"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/40 to-slate-950/95" />
            <div className="relative px-6 py-12 text-center sm:px-12">
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-amber-300">
                <Gamepad2 className="h-3.5 w-3.5" /> Seria gier edukacyjnych
              </span>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                DigiLib <span className="gradient-text">World</span>
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                Wokselowe wyspy pełne zagadek, mostów i skarbów. Dziecko uczy się przez przygodę —
                Ty płacisz raz, a gra zostaje z Wami na zawsze.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
                <span className="rounded-full border border-cyan-400/30 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-cyan-200 backdrop-blur" data-testid="games-chip-single">
                  Gra: 49,99 zł
                </span>
                <span className="rounded-full border border-amber-400/40 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-amber-200 backdrop-blur" data-testid="games-chip-bundle">
                  Pakiet 7 gier: 199 zł
                </span>
                <span className="rounded-full border border-violet-400/40 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-violet-200 backdrop-blur" data-testid="games-chip-kids">
                  Pakiet KIDS: 249 zł
                </span>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* WIELKIE PAKIETY — wyróżnione, większe niż zwykłe karty */}
      <div className="mt-10 space-y-6">
        {bundleGames && (
          <BundleCard
            testid="bundle-games-card"
            img="/img/banners/digilib-world-campaign.jpeg"
            badge={<><Package className="h-3.5 w-3.5" /> Pakiet wszystkich gier — najlepsza cena</>}
            title="Wszystkie 7 gier DigiLib World w jednym pakiecie"
            desc="Siedem wokselowych światów edukacyjnych z Libi i Digim. Kupujesz raz — cała seria jest Twoja na zawsze, z comiesięcznymi aktualizacjami."
            items={GAME_NAMES}
            price="199"
            oldPrice="349,93 zł"
            note="Oszczędzasz ponad 150 zł względem zakupu osobno"
            tall
          />
        )}
        {bundleKids && (
          <BundleCard
            testid="bundle-kids-card"
            img="/img/banners/digilib-kids-campaign.jpeg"
            badge={<><Gift className="h-3.5 w-3.5" /> Pakiet KIDS — komplet dla dziecka</>}
            title="Wszystko dla dzieci: aplikacje DigiKids + 7 gier"
            desc="DigiKids: Matematyka przez Zabawę i Nauka Czytania oraz cała seria DigiLib World — 9 produktów w jednym prezentowym pakiecie."
            items={["DigiKids: Matematyka", "DigiKids: Czytanie", ...GAME_NAMES]}
            price="249"
            oldPrice="429,91 zł"
            note="Oszczędzasz ponad 180 zł — idealny prezent"
            tall
          />
        )}
      </div>

      {/* SIATKA GIER */}
      <Reveal delay={120}>
        <div className="mt-12 flex items-end justify-between">
          <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
          Gry <span className="gradient-text">edukacyjne</span>
          </h3>
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            <Sparkles className="h-3.5 w-3.5 text-amber-400" /> Nowe przedmioty w drodze
          </span>
        </div>
      </Reveal>
      <div className="mt-6">
        <ProductGrid products={singles} testid="games-grid" />
      </div>
    </section>
  );
}
